import { type RenderableTreeNode, Tag } from "@markdoc/markdoc";

import { DOCS_ORIGIN } from "../../config/rules/endpoints";
import type { DocEntry } from "./registry";

/**
 * @mountayaapp/docs clean-Markdown extraction. This render-free module turns a
 * parsed content entry into self-contained Markdown suitable for ingestion by a
 * human reader or a language model: the page title as an H1, the frontmatter
 * description as a lead paragraph, then the body walked from the same transformed
 * Markdoc tree the renderers and the search index read. Shared partials are
 * already expanded into that tree when the registry parses a page, so a page's
 * reused enum and parameter tables come through without a separate inlining pass.
 *
 * The documentation tags reduce to plain Markdown a reader takes at face value:
 * code groups unwrap to fenced blocks, tabs and accordions to `###` subsections,
 * steps to a bold-numbered sequence, properties to a bullet list, alerts to
 * blockquotes, cards to a bulleted link list, and a hero folds into the lead. The
 * purely-interactive embeds (the map studio, the GraphQL playground, the OpenAPI
 * reference, the changelog) carry no static text, so each drops to a one-line note
 * linking to the live page. The output is one string both the build-time emission
 * and the in-page control produce identically for a given entry.
 */

/**
 * EntryMarkdownOptions tunes an entryToMarkdown call. `origin` is the absolute
 * origin the interactive-embed notes link back to; it defaults to the public docs
 * origin so both the build and the client produce identical, absolute links, and
 * a test overrides it to assert the URL without depending on the environment.
 */
export interface EntryMarkdownOptions {
  origin?: string;
}

/**
 * SerializeContext threads the values a block needs that are not on its own node.
 * `pageUrl` is the absolute URL of the page being serialized, so an interactive
 * embed can point a reader at the live page it was dropped from.
 */
interface SerializeContext {
  pageUrl: string;
}

/**
 * BLOCK_NAMES are the render names that begin their own block in the reduced
 * Markdown. Everything else a container holds (text, emphasis, links, inline code)
 * is inline and is gathered into a paragraph between blocks.
 */
const BLOCK_NAMES = new Set<string>([
  "Accordion",
  "Alert",
  "blockquote",
  "Card",
  "CardGroup",
  "Changelog",
  "CodeBlock",
  "CodeGroup",
  "Element",
  "GraphiQL",
  "Heading",
  "Hero",
  "hr",
  "Iframe",
  "Image",
  "MapEmbedding",
  "ol",
  "Paragraph",
  "Properties",
  "Property",
  "Redoc",
  "Steps",
  "Table",
  "Tabs",
  "ul",
]);

/**
 * stringAttr reads a Markdoc tag attribute as a string, returning an empty string
 * when the attribute is absent or not a string, so callers never guard each read.
 */
const stringAttr = (tag: Tag, key: string): string => {
  const value = tag.attributes[key];

  return typeof value === "string" ? value : "";
};

/**
 * numberAttr reads a Markdoc tag attribute as a number, falling back when the
 * attribute is absent or not a number.
 */
const numberAttr = (tag: Tag, key: string, fallback: number): number => {
  const value = tag.attributes[key];

  return typeof value === "number" ? value : fallback;
};

/**
 * collectItems picks a container's own item children by render name. An item is
 * not always a direct child of the container that owns it: Markdoc wraps a tag
 * authored inline — a call-to-action button on its own line, a card written as a
 * single line — in a `Paragraph`, and expands a shared partial into an array of
 * nodes. This walks those two wrappers and nothing else. A matched item is taken
 * whole, since its children are the item's body rather than more items, and any
 * other container is left to its own reducer, so document order is preserved and
 * a nested list's items never surface in its parent.
 */
const collectItems = (children: RenderableTreeNode[], name: string): Tag[] => {
  const items: Tag[] = [];

  const walk = (nodes: RenderableTreeNode[]): void => {
    for (const node of nodes) {
      if (Array.isArray(node)) {
        walk(node);
        continue;
      }

      if (!Tag.isTag(node)) {
        continue;
      }

      if (node.name === name) {
        items.push(node);
      } else if (node.name === "Paragraph") {
        walk(node.children);
      }
    }
  };

  walk(children);

  return items;
};

/**
 * collapseSpace collapses every run of whitespace to a single space and trims the
 * ends, so prose stitched from many text and inline nodes reads as one clean line.
 */
const collapseSpace = (text: string): string => text.replace(/\s+/g, " ").trim();

/**
 * collectStringLeaves concatenates the raw string leaves of a subtree. It backs
 * the fallback path for a fenced block whose code shipped as text children rather
 * than a `content` attribute.
 */
const collectStringLeaves = (node: RenderableTreeNode | RenderableTreeNode[]): string => {
  if (typeof node === "string") {
    return node;
  }

  if (Array.isArray(node)) {
    return node.map(collectStringLeaves).join("");
  }

  if (Tag.isTag(node)) {
    return collectStringLeaves(node.children);
  }

  return "";
};

/**
 * renderInline serializes inline content to Markdown: text passes through,
 * emphasis and inline code get their marks, links and buttons become `[text](href)`,
 * and an inline image becomes `![alt](src)`. Block tags never reach here; a
 * container gathers its inline children and hands them to this function as a group.
 */
const renderInline = (node: RenderableTreeNode | RenderableTreeNode[]): string => {
  if (typeof node === "string") {
    return node;
  }

  if (Array.isArray(node)) {
    return node.map(renderInline).join("");
  }

  if (!Tag.isTag(node)) {
    return "";
  }

  const children = (): string => renderInline(node.children);

  switch (node.name) {
    case "InlineCode":
      return `\`${stringAttr(node, "content")}\``;
    case "Link":
    case "Button":
      return `[${children()}](${stringAttr(node, "href")})`;
    case "strong":
      return `**${children()}**`;
    case "em":
      return `*${children()}*`;
    case "s":
      return `~~${children()}~~`;
    case "br":
      return "\n";
    case "Image":
      return `![${stringAttr(node, "alt")}](${stringAttr(node, "src")})`;
    default:
      return children();
  }
};

/**
 * renderInlineText renders an inline subtree to a single clean line of Markdown.
 */
const renderInlineText = (nodes: RenderableTreeNode[]): string =>
  collapseSpace(renderInline(nodes));

/**
 * fence renders a code block: a fenced block carrying its language, optionally
 * captioned with a bold filename or tab label above it so the origin of the
 * snippet survives without corrupting the code.
 */
const fence = (caption: string, language: string, content: string): string => {
  const body = content.replace(/\n+$/, "");
  const block = `\`\`\`${language}\n${body}\n\`\`\``;

  return caption !== "" ? `**${caption}**\n\n${block}` : block;
};

/**
 * blockquote prefixes every line of a block with `> `, so a nested block renders
 * as one quoted region. It backs both alerts and the interactive-embed notes.
 */
const blockquote = (text: string): string =>
  text
    .split("\n")
    .map((line) => (line === "" ? ">" : `> ${line}`))
    .join("\n");

/**
 * interactiveNote is the placeholder a purely-interactive embed reduces to: a
 * quoted sentence pointing a reader at the live page, since the embed itself has
 * no static text to carry into Markdown.
 */
const interactiveNote = (sentence: string, pageUrl: string): string =>
  blockquote(`${sentence} View it live at ${pageUrl}.`);

/**
 * renderHeading renders a heading at its own level, clamped to the Markdown range.
 */
const renderHeading = (node: Tag): string => {
  const level = Math.min(Math.max(numberAttr(node, "level", 2), 1), 6);

  return `${"#".repeat(level)} ${renderInlineText(node.children)}`;
};

/**
 * renderCodeBlock renders a standalone fenced block, preferring the `content`
 * attribute the tag transform folds in and falling back to raw text children.
 */
const renderCodeBlock = (node: Tag): string => {
  const caption = stringAttr(node, "filename") || stringAttr(node, "label");
  const content = stringAttr(node, "content") || collectStringLeaves(node.children);

  return fence(caption, stringAttr(node, "language"), content);
};

/**
 * renderCodeGroup unwraps a tabbed code group to its fenced blocks in order, each
 * captioned with its tab label, so the group reads as a plain sequence of snippets.
 */
const renderCodeGroup = (node: Tag): string => {
  const raw = node.attributes.tabs;
  const tabs = Array.isArray(raw) ? raw : [];

  return tabs
    .map((tab) => {
      const label = typeof tab?.label === "string" ? tab.label : "";
      const language = typeof tab?.language === "string" ? tab.language : "";
      const content = typeof tab?.content === "string" ? tab.content : "";

      return fence(label, language, content);
    })
    .join("\n\n");
};

/**
 * renderProperty renders one API property row as a bullet: its name as inline
 * code, a parenthetical of the metadata that is present (type, whether it is
 * required, its default), then the description.
 */
const renderProperty = (node: Tag): string => {
  const name = stringAttr(node, "name");
  const type = stringAttr(node, "type");
  const required = node.attributes.required === true;
  const defaultValue = stringAttr(node, "default");

  const meta: string[] = [];
  if (type !== "") {
    meta.push(type);
  }

  if (required) {
    meta.push("required");
  }

  if (defaultValue !== "") {
    meta.push(`default: \`${defaultValue}\``);
  }

  const head = `\`${name}\`${meta.length > 0 ? ` (${meta.join(", ")})` : ""}`;
  const body = renderInlineText(node.children);

  return body !== "" ? `- ${head}: ${body}` : `- ${head}`;
};

/**
 * renderCard renders one card as a bullet linking to its target, followed by its
 * description, so a card group becomes a scannable list of links.
 */
const renderCard = (node: Tag): string => {
  const title = stringAttr(node, "title");
  const href = stringAttr(node, "href");
  const body = renderInlineText(node.children);
  const label = title !== "" ? title : href !== "" ? href : body;
  const link = href !== "" ? `[${label}](${href})` : `**${label}**`;

  return body !== "" && body !== label ? `- ${link}: ${body}` : `- ${link}`;
};

/**
 * renderHero folds a hero into the lead: its title is dropped (the page H1 already
 * carries it), its subtitle becomes a paragraph, and its call-to-action buttons
 * become a bulleted link list.
 */
const renderHero = (node: Tag): string => {
  const subtitle = collapseSpace(stringAttr(node, "subtitle"));
  const buttons = collectItems(node.children, "Button").map(
    (button) => `- [${renderInlineText(button.children)}](${stringAttr(button, "href")})`,
  );

  const parts: string[] = [];
  if (subtitle !== "") {
    parts.push(subtitle);
  }

  if (buttons.length > 0) {
    parts.push(buttons.join("\n"));
  }

  return parts.join("\n\n");
};

/**
 * renderTable renders a Markdoc table as a GitHub-flavored Markdown table. Every
 * table row is collected in document order across the head and body; the first is
 * the header, the rest are data rows, and cell pipes are escaped so a cell value
 * cannot break the column layout.
 */
const renderTable = (node: Tag): string => {
  const rows: Tag[] = [];
  const collectRows = (current: Tag): void => {
    for (const child of current.children) {
      if (!Tag.isTag(child)) {
        continue;
      }

      if (child.name === "TableTr") {
        rows.push(child);
      } else {
        collectRows(child);
      }
    }
  };

  collectRows(node);

  const firstRow = rows[0];
  if (!firstRow) {
    return "";
  }

  const cells = (row: Tag): string[] =>
    row.children
      .filter(
        (cell): cell is Tag =>
          Tag.isTag(cell) && (cell.name === "TableTh" || cell.name === "TableTd"),
      )
      .map((cell) => renderInlineText(cell.children).replace(/\|/g, "\\|"));

  const header = cells(firstRow);
  const line = (values: string[]): string => `| ${values.join(" | ")} |`;
  const divider = `| ${header.map(() => "---").join(" | ")} |`;
  const bodyLines = rows.slice(1).map((row) => line(cells(row)));

  return [line(header), divider, ...bodyLines].join("\n");
};

/**
 * renderBlock serializes one block-level node to its Markdown, delegating each
 * container to its own reducer. An unrecognized wrapper renders its children as
 * blocks so nothing is silently dropped.
 */
const renderBlock = (node: Tag, context: SerializeContext): string => {
  switch (node.name) {
    case "Heading":
      return renderHeading(node);
    case "Paragraph":
      return renderInlineText(node.children);
    case "CodeBlock":
      return renderCodeBlock(node);
    case "CodeGroup":
      return renderCodeGroup(node);
    case "Alert": {
      const title = stringAttr(node, "title");
      const body = renderBlocks(node.children, context);
      const inner =
        title !== "" ? (body !== "" ? `**${title}**\n\n${body}` : `**${title}**`) : body;

      return blockquote(inner);
    }
    case "CardGroup":
      return collectItems(node.children, "Card").map(renderCard).join("\n");
    case "Card":
      return renderCard(node);
    case "Properties":
      return collectItems(node.children, "Property").map(renderProperty).join("\n");
    case "Property":
      return renderProperty(node);
    case "Steps":
      return collectItems(node.children, "Step")
        .map((step, index) => {
          const ordinal = numberAttr(step, "ordinal", index + 1);
          const title = stringAttr(step, "title");
          const head = title !== "" ? `**${ordinal}. ${title}**` : `**Step ${ordinal}**`;
          const body = renderBlocks(step.children, context);

          return body !== "" ? `${head}\n\n${body}` : head;
        })
        .join("\n\n");
    case "Tabs":
      return collectItems(node.children, "Tab")
        .map((tab) => {
          const label = stringAttr(tab, "label") || "Tab";
          const body = renderBlocks(tab.children, context);

          return body !== "" ? `### ${label}\n\n${body}` : `### ${label}`;
        })
        .join("\n\n");
    case "Accordion":
      return collectItems(node.children, "AccordionItem")
        .map((item) => {
          const label = stringAttr(item, "label") || stringAttr(item, "title") || "Section";
          const body = renderBlocks(item.children, context);

          return body !== "" ? `### ${label}\n\n${body}` : `### ${label}`;
        })
        .join("\n\n");
    case "Hero":
      return renderHero(node);
    case "Table":
      return renderTable(node);
    case "ul":
      return renderList(node, context, false);
    case "ol":
      return renderList(node, context, true);
    case "blockquote":
      return blockquote(renderBlocks(node.children, context));
    case "hr":
      return "---";
    case "Image":
      return `![${stringAttr(node, "alt")}](${stringAttr(node, "src")})`;
    case "MapEmbedding":
      return interactiveNote("This section embeds a live interactive map.", context.pageUrl);
    case "Element":
      return interactiveNote(
        "This section renders a live example of the element described here.",
        context.pageUrl,
      );
    case "GraphiQL":
      return interactiveNote(
        "This section embeds the interactive GraphQL playground.",
        context.pageUrl,
      );
    case "Redoc":
      return interactiveNote("This section embeds the interactive API reference.", context.pageUrl);
    case "Changelog":
      return interactiveNote("This section renders the release changelog.", context.pageUrl);
    case "Iframe": {
      const src = stringAttr(node, "src");
      const title = stringAttr(node, "title") || src;

      return blockquote(`Embedded content: [${title}](${src}).`);
    }
    default:
      return renderBlocks(node.children, context);
  }
};

/**
 * renderListItem renders one list item behind its marker: the item body is
 * rendered as blocks, the first line takes the marker, and every continuation line
 * is indented to the marker's width so nested lists and multi-block items keep
 * their structure.
 */
const renderListItem = (item: Tag, marker: string, context: SerializeContext): string => {
  const body = renderBlocks(item.children, context) || renderInlineText(item.children);
  const indent = " ".repeat(marker.length);

  return body
    .split("\n")
    .map((line, index) =>
      index === 0 ? `${marker}${line}` : line === "" ? "" : `${indent}${line}`,
    )
    .join("\n");
};

/**
 * renderList renders a bullet or ordered list, numbering ordered items from one.
 * Nested lists carried inside an item are indented by the item's own marker width,
 * so arbitrary nesting reads correctly without tracking a depth.
 */
const renderList = (node: Tag, context: SerializeContext, ordered: boolean): string =>
  collectItems(node.children, "li")
    .map((item, index) => renderListItem(item, ordered ? `${index + 1}. ` : "- ", context))
    .join("\n");

/**
 * renderBlocks serializes a container's children into a stack of blocks. Runs of
 * inline nodes between block tags are gathered into a paragraph, so both tight
 * lists (inline item bodies) and loose prose (paragraph-wrapped) reduce cleanly.
 */
const renderBlocks = (children: RenderableTreeNode[], context: SerializeContext): string => {
  const blocks: string[] = [];
  let inlineBuffer: RenderableTreeNode[] = [];

  const flushInline = (): void => {
    if (inlineBuffer.length === 0) {
      return;
    }

    const text = renderInlineText(inlineBuffer);
    inlineBuffer = [];

    if (text !== "") {
      blocks.push(text);
    }
  };

  const walk = (nodes: RenderableTreeNode[]): void => {
    for (const node of nodes) {
      if (Array.isArray(node)) {
        walk(node);
        continue;
      }

      if (Tag.isTag(node) && BLOCK_NAMES.has(node.name)) {
        flushInline();
        const rendered = renderBlock(node, context);

        if (rendered.trim() !== "") {
          blocks.push(rendered);
        }

        continue;
      }

      inlineBuffer.push(node);
    }
  };

  walk(children);
  flushInline();

  return blocks.join("\n\n");
};

/**
 * dropLeadingDuplicate removes a leading block that merely restates the frontmatter
 * description, so a hero subtitle echoing the description does not appear twice in
 * the lead.
 */
const dropLeadingDuplicate = (body: string, description: string): string => {
  const trimmed = description.trim();

  if (trimmed !== "" && body.startsWith(trimmed)) {
    return body.slice(trimmed.length).replace(/^\n+/, "");
  }

  return body;
};

/**
 * entryToMarkdown turns a parsed content entry into clean, self-contained Markdown.
 * The page title leads as an H1 and the frontmatter description as a paragraph
 * beneath it; the body is the transformed Markdoc tree reduced to plain Markdown,
 * with shared partials already inlined by the registry's parse. Both the build's
 * per-page `.md` emission and the in-page "copy for LLM" control call this with an
 * entry and get the same string.
 */
export const entryToMarkdown = (entry: DocEntry, options?: EntryMarkdownOptions): string => {
  const origin = options?.origin ?? DOCS_ORIGIN;
  const context: SerializeContext = { pageUrl: `${origin}${entry.href}` };

  const description = (entry.frontmatter.description ?? "").trim();
  const root = entry.tree;
  const rootChildren = Tag.isTag(root) ? root.children : Array.isArray(root) ? root : [root];
  const body = dropLeadingDuplicate(renderBlocks(rootChildren, context), description);

  const parts: string[] = [`# ${entry.frontmatter.title}`];
  if (description !== "") {
    parts.push(description);
  }

  if (body !== "") {
    parts.push(body);
  }

  return `${parts.join("\n\n").trimEnd()}\n`;
};
